import { useCallback, useEffect, useMemo, useState, type ReactElement } from 'react'
import { useIntl, FormattedMessage } from 'react-intl'
import { rem } from 'polished'
import styled from 'styled-components'

import PropertyMap from 'components/Common/PropertyMap'
import type { PropertyMapOptions } from 'components/Common/PropertyMap/types'
import Pane from 'components/Common/Pane'
import VerticalSpacer from 'components/Common/Spacing/VerticalSpacer'
import { mediaQueryUp } from 'components/utils/breakpoint'
import LineBuildingIcon from 'LuxKit/Icons/line/LineBuildingIcon'
import LineBusAltIcon from 'LuxKit/Icons/line/LineBusAltIcon'
import LinePlaneFlyIcon from 'LuxKit/Icons/line/LinePlaneFlyIcon'
import ModalContent from 'LuxKit/Modal/ModalContent'
import TabButton from 'LuxKit/Tabs/TabButton'
import TabButtonsGroup from 'LuxKit/Tabs/TabButtonsGroup'
import BodyTextBlock from 'LuxKit/TextBlocks/BodyTextBlock'
import BodyText from 'LuxKit/Typography/BodyText'
import Typography from 'LuxKit/Typography/Typography'
import useCarHireLabels from 'hooks/CarHire/useCarHireLabels'
import {
  DROP_OFF_LOCATION_TAB_ID,
  PICK_UP_LOCATION_TAB_ID,
  type CarHireLocationTabId,
} from './carHireLocationTabs'

const MapContainer = styled.div`
  height: ${rem(375)};

  ${mediaQueryUp.tablet} {
    height: ${rem(280)};
  }
`

const Content = styled(VerticalSpacer)`
  padding-top: ${rem(20)};

  ${mediaQueryUp.tablet} {
    padding-top: ${rem(24)};
  }
`

const PickUpTypeSection = styled(Pane)`
  padding: ${rem(16)};
`

const pickupTypeIconMap = new Map<App.CarHirePickupType, ReactElement>([
  // oxlint-disable-next-line react/jsx-key
  ['office', <LineBuildingIcon />],
  // oxlint-disable-next-line react/jsx-key
  ['shuttle', <LinePlaneFlyIcon />],
  // oxlint-disable-next-line react/jsx-key
  ['terminal', <LineBusAltIcon />],
])

const MAP_OPTIONS: PropertyMapOptions = {
  fullscreenControl: false,
}

const PROPERTY_MAP_WRAPPER_STYLE = { height: '100%' } as const

export interface CarHireLocationModalContentLocation {
  id: string
  name: string
  address: string
  marker: App.MapMarker
  openingGroups: Array<{
    dayStart: string
    hours: string
  }>
  pickupType?: App.CarHirePickupType
}

interface Props {
  pickUpLocation: CarHireLocationModalContentLocation
  dropOffLocation: CarHireLocationModalContentLocation
  openTab: CarHireLocationTabId
}

function CarHireLocationModalContent({
  pickUpLocation,
  dropOffLocation,
  openTab,
}: Props) {
  const intl = useIntl()
  const { pickupTypeLabelMap } = useCarHireLabels()
  const differentLocations = pickUpLocation.id !== dropOffLocation.id
  const [activeTabId, setActiveTabId] = useState<CarHireLocationTabId>(openTab)

  useEffect(() => {
    setActiveTabId(openTab)
  }, [openTab])

  const pickUpTabLabel = intl.formatMessage({
    id: 'Vrej93gQdG49k7mAGZey',
    defaultMessage: 'Pick-up',
    description: 'Tab label for pick-up location in car hire location modal',
  })
  const dropOffTabLabel = intl.formatMessage({
    id: 'Pl-8onaTJOzpbr1hN0Pd',
    defaultMessage: 'Drop-off',
    description: 'Tab label for drop-off location in car hire location modal',
  })
  const addressLabel = intl.formatMessage({
    id: 'QgCrJ1umsYspnwAt9meW',
    defaultMessage: 'Address',
    description: 'Label for address section in car hire location modal',
  })
  const openingHoursLabel = intl.formatMessage({
    id: 'BCoEeOD-ztdrAtu9LdNM',
    defaultMessage: 'Opening hours',
    description: 'Label for opening hours section in car hire location modal',
  })

  const activeLocation = activeTabId === PICK_UP_LOCATION_TAB_ID
    ? pickUpLocation
    : dropOffLocation
  const activeMarkers = useMemo(() => [activeLocation.marker], [activeLocation.marker])
  const pickupTypeIcon = activeLocation.pickupType
    ? pickupTypeIconMap.get(activeLocation.pickupType)
    : undefined
  const pickupTypeLabel = activeLocation.pickupType
    ? pickupTypeLabelMap.get(activeLocation.pickupType)
    : undefined
  const handlePickUpTabClick = useCallback(() => setActiveTabId(PICK_UP_LOCATION_TAB_ID), [])
  const handleDropOffTabClick = useCallback(() => setActiveTabId(DROP_OFF_LOCATION_TAB_ID), [])

  return (
    <>
      {differentLocations && (
        <TabButtonsGroup fit="grow">
          <TabButton
            isActive={activeTabId === PICK_UP_LOCATION_TAB_ID}
            size="medium"
            onClick={handlePickUpTabClick}
          >
            {pickUpTabLabel}
          </TabButton>
          <TabButton
            isActive={activeTabId === DROP_OFF_LOCATION_TAB_ID}
            size="medium"
            onClick={handleDropOffTabClick}
          >
            {dropOffTabLabel}
          </TabButton>
        </TabButtonsGroup>
      )}
      <MapContainer>
        <PropertyMap
          height="100%"
          wrapperStyle={PROPERTY_MAP_WRAPPER_STYLE}
          markers={activeMarkers}
          mapOptions={MAP_OPTIONS}
        />
      </MapContainer>
      <ModalContent>
        <Content gap={24}>
          <div>
            <BodyText variant="large" weight="bold">{addressLabel}</BodyText>
            <BodyText variant="large">
              {activeLocation.name}<br />
              {activeLocation.address}
            </BodyText>
          </div>

          {pickupTypeIcon && pickupTypeLabel && (
            <PickUpTypeSection background="light-background">
              <BodyTextBlock
                variant="medium"
                startIcon={pickupTypeIcon}
              >
                {pickupTypeLabel}
              </BodyTextBlock>
            </PickUpTypeSection>
          )}

          <div>
            <BodyText variant="large" weight="bold">{openingHoursLabel}</BodyText>
            {activeLocation.openingGroups.map((day) => (
              <BodyText key={day.dayStart} variant="large">
                <FormattedMessage
                  id="ohHjFDtpl5JwHAACRcOT"
                  defaultMessage="{dayName}: {hours}"
                  description="Opening hours for a day of the week at a car hire location, e.g. Monday: 8:00 AM - 6:00 PM"
                  values={{
                    dayName: <Typography format="titlecase" as="span">{day.dayStart}</Typography>,
                    hours: day.hours,
                  }}
                />
              </BodyText>
            ))}
          </div>
        </Content>
      </ModalContent>
    </>
  )
}

export default CarHireLocationModalContent
